'use client';

import {
  Children,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type SyntheticEvent,
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import classnames from 'classnames';
import {
  LuArrowDown,
  LuArrowDownUp,
  LuArrowUp,
  LuEllipsisVertical,
  LuInfo,
} from 'react-icons/lu';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

import { Badge } from '../badge';
import { DropdownMenu, DropdownMenuItem } from '../dropdown-menu';
import { Checkbox } from '../forms/checkbox/Checkbox';
import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../tooltip';
import type {
  Key,
  SelectionMode,
  SortDescriptor,
  SortDirection,
  TableActionsProps,
  TableBodyProps,
  TableCardHeaderProps,
  TableCardRootProps,
  TableCellProps,
  TableHeadProps,
  TableHeaderProps,
  TableRootProps,
  TableRowProps,
} from './Table.types';

import styles from './Table.module.scss';

const labels = {
  actions: 'Acciones',
  selectAll: 'Seleccionar todo',
  selectRow: 'Seleccionar fila',
  selectRowNamed: (id: Key) => `Seleccionar fila ${id}`,
  sortAscending: 'Ordenado de forma ascendente',
  sortDescending: 'Ordenado de forma descendente',
  sortable: 'Ordenable',
};

interface TableContextValue {
  size?: 'sm' | 'md';
}

const TableContext = createContext<TableContextValue>({});

const useTableContext = () => useContext(TableContext);

const useTableSize = () => useTableContext().size ?? 'md';

interface TableSortContextValue {
  sortDescriptor?: SortDescriptor;
  onSortChange?: (descriptor: SortDescriptor) => void;
}

const TableSortContext = createContext<TableSortContextValue>({});

const useTableSortContext = () => useContext(TableSortContext);

/**
 * Stable selection config — its identity never changes between renders (only
 * when `selectionMode` does), so row registration effects don't re-run every
 * time the selection changes.
 */
interface TableSelectionConfig {
  selectionMode: SelectionMode;
  registerRow: (key: Key, isDisabled: boolean) => () => void;
}

const TableSelectionConfigContext = createContext<TableSelectionConfig>({
  selectionMode: 'none',
  registerRow: () => () => {},
});

const useTableSelectionConfig = () => useContext(TableSelectionConfigContext);

/** Dynamic selection state — changes whenever the selection changes. */
interface TableSelectionState {
  isSelected: (key: Key) => boolean;
  toggle: (key: Key) => void;
  toggleAll: () => void;
  allSelected: boolean;
  someSelected: boolean;
}

const TableSelectionStateContext = createContext<TableSelectionState>({
  isSelected: () => false,
  toggle: () => {},
  toggleAll: () => {},
  allSelected: false,
  someSelected: false,
});

const useTableSelectionState = () => useContext(TableSelectionStateContext);

/**
 * Render either static children or, when a collection (`items`/`columns`) and a
 * render function are provided, one keyed node per item. The key is taken from
 * the rendered element's `id` prop, falling back to its existing key or the
 * index.
 */
function renderCollection<T>(
  items: Iterable<T> | undefined,
  children: ReactNode | ((item: T) => ReactNode),
): ReactNode {
  if (items && typeof children === 'function') {
    const render = children as (item: T) => ReactNode;
    return Array.from(items, (item, index) => {
      const node = render(item);
      if (isValidElement(node)) {
        const id = (node.props as { id?: Key }).id;
        return cloneElement(node, { key: node.key ?? id ?? index });
      }
      return node;
    });
  }

  return children as ReactNode;
}

const TableCardRoot = ({
  children,
  className,
  size = 'sm',
  testId = 'ui-table-card',
  ...props
}: TableCardRootProps) => {
  const sizeContext = useMemo<TableContextValue>(() => ({ size }), [size]);

  return (
    <TableContext.Provider value={sizeContext}>
      <div
        {...props}
        data-testid={testId}
        className={classnames(
          styles.context,
          styles[`context--${size}`],
          className,
        )}
      >
        {children}
      </div>
    </TableContext.Provider>
  );
};

const SortIcon = ({ sortDirection }: { sortDirection?: SortDirection }) => {
  if (sortDirection === 'ascending') {
    return (
      <IconButton
        icon={<LuArrowUp />}
        size="xs"
        aria-label={labels.sortAscending}
      />
    );
  }
  if (sortDirection === 'descending') {
    return (
      <IconButton
        icon={<LuArrowDown />}
        size="xs"
        aria-label={labels.sortDescending}
      />
    );
  }
  return (
    <IconButton
      icon={<LuArrowDownUp />}
      size="xs"
      aria-label={labels.sortable}
    />
  );
};

SortIcon.displayName = 'SortIcon';

const TableCardHeader = ({
  title,
  badge,
  description,
  contentTrailing,
  className,
}: TableCardHeaderProps) => {
  const size = useTableSize();

  return (
    <div
      className={classnames(
        styles.cardHeader,
        styles[`cardHeader--${size}`],
        className,
      )}
    >
      <div className={styles.cardHeader__main}>
        <div className={styles.cardHeader__head}>
          <span className={styles.cardHeader__title}>{title}</span>
          {badge ? (
            isValidElement(badge) ? (
              badge
            ) : (
              <Badge bg="featured">{badge}</Badge>
            )
          ) : null}
        </div>
        {description && (
          <span className={styles.cardHeader__description}>{description}</span>
        )}
      </div>
      {contentTrailing}
    </div>
  );
};

TableCardHeader.displayName = 'TableCardHeader';

const TableRoot = ({
  className,
  size,
  sortDescriptor,
  onSortChange,
  selectionMode = 'none',
  selectedKeys,
  defaultSelectedKeys,
  onSelectionChange,
  children,
  testId = 'ui-table',
  ...props
}: TableRootProps) => {
  const context = useTableContext();

  const resolvedSize = size ?? context.size ?? 'md';

  const [internalSelected, setInternalSelected] = useState<Set<Key>>(
    () => new Set(defaultSelectedKeys ?? []),
  );
  // Registry of every rendered row's key → isDisabled. Rows register
  // themselves (see TableRow), giving the header's "select all" the universe of
  // currently rendered keys — which makes pagination behave correctly, since
  // each page only registers its own rows.
  const [rows, setRows] = useState<Map<Key, boolean>>(() => new Map());

  const isControlled = selectedKeys !== undefined;
  const selected = selectedKeys ?? internalSelected;

  const applySelection = useCallback(
    (next: Set<Key>) => {
      if (!isControlled) {
        setInternalSelected(next);
      }
      onSelectionChange?.(next);
    },
    [isControlled, onSelectionChange],
  );

  const registerRow = useCallback((key: Key, isDisabled: boolean) => {
    setRows((prev) => {
      if (prev.get(key) === isDisabled) {
        return prev;
      }
      const next = new Map(prev);
      next.set(key, isDisabled);
      return next;
    });
    return () => {
      setRows((prev) => {
        if (!prev.has(key)) {
          return prev;
        }
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    };
  }, []);

  const selectionConfig = useMemo<TableSelectionConfig>(
    () => ({ selectionMode, registerRow }),
    [selectionMode, registerRow],
  );

  const selectableKeys = useMemo(
    () =>
      [...rows.entries()]
        .filter(([, isDisabled]) => !isDisabled)
        .map(([key]) => key),
    [rows],
  );

  const allSelected =
    selectableKeys.length > 0 &&
    selectableKeys.every((key) => selected.has(key));
  const someSelected = selectableKeys.some((key) => selected.has(key));

  const selectionState = useMemo<TableSelectionState>(
    () => ({
      isSelected: (key: Key) => selected.has(key),
      toggle: (key: Key) => {
        if (selectionMode === 'single') {
          applySelection(
            selected.has(key) ? new Set<Key>() : new Set<Key>([key]),
          );
          return;
        }
        const next = new Set(selected);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        applySelection(next);
      },
      toggleAll: () => {
        const next = new Set(selected);
        if (allSelected) {
          selectableKeys.forEach((key) => next.delete(key));
        } else {
          selectableKeys.forEach((key) => next.add(key));
        }
        applySelection(next);
      },
      allSelected,
      someSelected,
    }),
    [
      selected,
      selectionMode,
      selectableKeys,
      allSelected,
      someSelected,
      applySelection,
    ],
  );

  const sizeContext = useMemo<TableContextValue>(
    () => ({ size: resolvedSize }),
    [resolvedSize],
  );

  const sortContext = useMemo<TableSortContextValue>(
    () => ({ sortDescriptor, onSortChange }),
    [sortDescriptor, onSortChange],
  );

  return (
    <TableContext.Provider value={sizeContext}>
      <TableSortContext.Provider value={sortContext}>
        <TableSelectionConfigContext.Provider value={selectionConfig}>
          <TableSelectionStateContext.Provider value={selectionState}>
            <div className={styles.tableRoot}>
              <table
                {...props}
                data-testid={testId}
                className={classnames(styles.table, className)}
              >
                {children}
              </table>
            </div>
          </TableSelectionStateContext.Provider>
        </TableSelectionConfigContext.Provider>
      </TableSortContext.Provider>
    </TableContext.Provider>
  );
};

TableRoot.displayName = 'Table';

const TableHeader = <T extends object>({
  columns,
  children,
  bordered = true,
  className,
  testId,
  ...props
}: TableHeaderProps<T>) => {
  const size = useTableSize();
  const { selectionMode } = useTableSelectionConfig();
  const { allSelected, someSelected, toggleAll } = useTableSelectionState();

  return (
    <thead
      {...props}
      data-testid={testId}
      className={classnames(
        styles.tableHeader,
        styles[`tableHeader--${size}`],
        bordered && styles['tableHeader--bordered'],
        className,
      )}
    >
      <tr>
        {selectionMode !== 'none' && (
          <th
            scope="col"
            className={classnames(
              styles.tableCell,
              styles[`tableCell--${size}`],
              styles['tableCell--selection'],
            )}
          >
            {selectionMode === 'multiple' && (
              <Checkbox
                aria-label={labels.selectAll}
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={toggleAll}
              />
            )}
          </th>
        )}
        {renderCollection(columns, children)}
      </tr>
    </thead>
  );
};

TableHeader.displayName = 'TableHeader';

const TableHead = ({
  className,
  tooltip,
  label,
  children,
  allowsSorting,
  id,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isRowHeader,
  onClick,
  testId,
  ...props
}: TableHeadProps) => {
  const size = useTableSize();
  const { sortDescriptor, onSortChange } = useTableSortContext();

  const sortDirection: SortDirection | undefined =
    allowsSorting && sortDescriptor && sortDescriptor.column === id
      ? sortDescriptor.direction
      : undefined;

  const handleClick = (event: MouseEvent<HTMLTableCellElement>) => {
    onClick?.(event);
    if (allowsSorting && onSortChange && id !== undefined) {
      onSortChange({
        column: id,
        direction: sortDirection === 'ascending' ? 'descending' : 'ascending',
      });
    }
  };

  return (
    <th
      {...props}
      scope="col"
      data-testid={testId}
      aria-sort={sortDirection ?? (allowsSorting ? 'none' : undefined)}
      onClick={allowsSorting || onClick ? handleClick : undefined}
      className={classnames(
        styles.tableHead,
        styles[`tableHead--${size}`],
        allowsSorting && styles['tableHead--sortable'],
        className,
      )}
    >
      <div className={styles.tableHead__header}>
        <div className={styles.tableHead__content}>
          {label && <span className={styles.tableHead__label}>{label}</span>}
          {children}
        </div>

        {tooltip && (
          <Tooltip>
            <TooltipTrigger asChild={true}>
              <Icon icon={<LuInfo />} size="xs" />
            </TooltipTrigger>

            <TooltipContent>{tooltip}</TooltipContent>
          </Tooltip>
        )}

        {allowsSorting && <SortIcon sortDirection={sortDirection} />}
      </div>
    </th>
  );
};

TableHead.displayName = 'TableHead';

const TableBody = <T extends object>({
  className,
  items,
  children,
  renderEmptyState,
  testId,
  ...props
}: TableBodyProps<T>) => {
  const itemArray = items ? Array.from(items) : undefined;
  const isEmpty =
    itemArray !== undefined
      ? itemArray.length === 0
      : typeof children !== 'function' &&
        Children.toArray(children).length === 0;

  return (
    <tbody
      {...props}
      data-testid={testId}
      data-empty={isEmpty || undefined}
      className={classnames(styles.tableBody, className)}
    >
      {isEmpty && renderEmptyState ? (
        <tr>
          {/* Browsers clamp colSpan to the actual column count, so this spans
              the whole row regardless of how many columns the table has. */}
          <td colSpan={9999}>{renderEmptyState()}</td>
        </tr>
      ) : (
        renderCollection(itemArray, children)
      )}
    </tbody>
  );
};

TableBody.displayName = 'TableBody';

const stopPropagation = (event: SyntheticEvent) => event.stopPropagation();

const TableRow = <T extends object>({
  columns,
  children,
  className,
  highlightSelectedRow = true,
  isSelected,
  isSelectable = true,
  isDisabled = false,
  onAction,
  onClick,
  onKeyDown,
  id,
  testId,
  ...props
}: TableRowProps<T>) => {
  const size = useTableSize();
  const { selectionMode, registerRow } = useTableSelectionConfig();
  const selection = useTableSelectionState();

  const showSelection = selectionMode !== 'none';

  // Register/unregister this row's key so the header's "select all" knows the
  // full set of selectable rows currently rendered.
  useIsomorphicLayoutEffect(() => {
    if (!showSelection || !isSelectable || id === undefined) {
      return;
    }
    return registerRow(id, isDisabled);
  }, [showSelection, isSelectable, id, isDisabled, registerRow]);

  const isActionable = !!onAction && !isDisabled;

  // Selection is checkbox-only: a row click only ever fires its action, never
  // toggles selection.
  const rowSelected =
    isSelected ??
    (showSelection && id !== undefined ? selection.isSelected(id) : false);

  const handleClick = (event: MouseEvent<HTMLTableRowElement>) => {
    onClick?.(event);
    if (isActionable) {
      onAction();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
    onKeyDown?.(event);
    if (
      isActionable &&
      event.target === event.currentTarget &&
      (event.key === 'Enter' || event.key === ' ')
    ) {
      event.preventDefault();
      onAction();
    }
  };

  return (
    <tr
      {...props}
      data-testid={testId}
      data-disabled={isDisabled || undefined}
      aria-disabled={isDisabled || undefined}
      tabIndex={isActionable ? 0 : undefined}
      onClick={onAction || onClick ? handleClick : undefined}
      onKeyDown={onAction || onKeyDown ? handleKeyDown : undefined}
      className={classnames(
        styles.tableRow,
        highlightSelectedRow &&
          rowSelected &&
          styles['tableRow--highlightSelected'],
        isActionable && styles['tableRow--actionable'],
        className,
      )}
    >
      {showSelection && (
        // Swallow the click so toggling selection never triggers the row action.
        <td
          className={classnames(
            styles.tableCell,
            styles[`tableCell--${size}`],
            styles['tableCell--selection'],
          )}
          onClick={stopPropagation}
          onPointerDown={stopPropagation}
        >
          {isSelectable && (
            <Checkbox
              aria-label={
                id !== undefined ? labels.selectRowNamed(id) : labels.selectRow
              }
              checked={rowSelected}
              disabled={isDisabled}
              onChange={() => {
                if (id !== undefined) {
                  selection.toggle(id);
                }
              }}
            />
          )}
        </td>
      )}
      {renderCollection(columns, children)}
    </tr>
  );
};

TableRow.displayName = 'TableRow';

const TableCell = ({
  className,
  children,
  isRowHeader,
  testId,
  ...props
}: TableCellProps) => {
  const size = useTableSize();

  const cellClassName = classnames(
    styles.tableCell,
    styles[`tableCell--${size}`],
    className,
  );

  if (isRowHeader) {
    return (
      <th {...props} scope="row" data-testid={testId} className={cellClassName}>
        {children}
      </th>
    );
  }

  return (
    <td {...props} data-testid={testId} className={cellClassName}>
      {children}
    </td>
  );
};

TableCell.displayName = 'TableCell';

const TableActions = ({
  children,
  label,
  placement = 'bottom-end',
  header = false,
  className,
  style,
  testId = 'ui-table-actions',
}: TableActionsProps) => {
  const size = useTableSize();
  const { selectionMode } = useTableSelectionConfig();

  const resolvedLabel = label ?? labels.actions;

  // With no actions to show, the three-dots trigger renders disabled rather
  // than opening an empty menu.
  const hasActions = Children.toArray(children).length > 0;

  // The header (bulk) menu only makes sense alongside row selection. The header
  // cell is always rendered (keeping the actions column aligned with the body),
  // but its trigger only shows when selection is on.
  const trigger =
    header && selectionMode === 'none' ? null : (
      // Swallow clicks so the three-dots trigger (and its menu) never also
      // fires the row's `onAction` — mirrors the selection cell's behaviour.
      <div
        className={styles.tableActions}
        onClick={stopPropagation}
        onPointerDown={stopPropagation}
      >
        <DropdownMenu
          aria-label={resolvedLabel}
          icon={<LuEllipsisVertical />}
          placement={placement}
          disabled={!hasActions}
        >
          {children}
        </DropdownMenu>
      </div>
    );

  // `Table.Actions` *is* the actions-column cell — no wrapping `Table.Head` /
  // `Table.Cell` needed. `header` renders the column header (`<th>`); otherwise
  // a body cell (`<td>`).
  if (header) {
    return (
      <th
        scope="col"
        data-testid={testId}
        style={style}
        className={classnames(
          styles.tableHead,
          styles[`tableHead--${size}`],
          className,
        )}
      >
        {trigger}
      </th>
    );
  }

  return (
    <td
      data-testid={testId}
      style={style}
      className={classnames(
        styles.tableCell,
        styles[`tableCell--${size}`],
        className,
      )}
    >
      {trigger}
    </td>
  );
};

TableActions.displayName = 'TableActions';

// A single menu entry for `Table.Actions` — a table-namespaced alias over
// `DropdownMenuItem` so callers can compose `Table.Action`s as children.
const TableAction = DropdownMenuItem;

const TableCard = {
  Root: TableCardRoot,
  Header: TableCardHeader,
};

const Table = TableRoot as typeof TableRoot & {
  Action: typeof TableAction;
  Actions: typeof TableActions;
  Body: typeof TableBody;
  Cell: typeof TableCell;
  Checkbox: typeof Checkbox;
  Head: typeof TableHead;
  Header: typeof TableHeader;
  Row: typeof TableRow;
};
Table.Action = TableAction;
Table.Actions = TableActions;
Table.Body = TableBody;
Table.Cell = TableCell;
Table.Checkbox = Checkbox;
Table.Head = TableHead;
Table.Header = TableHeader;
Table.Row = TableRow;

export { Table, TableCard };
